import React, { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useUser } from "../hooks/use-user";
import { Icons } from "../components/ui/icons";
import { Button } from "../components/ui/button";
import PageShell from "../components/page-shell";
import FilterField from "../components/mobile/filter-field";
import { DesktopOnly } from "../components/responsive";
import SelectInput from "../components/select-input";
import DataTable from "../components/data-table";
import { inventoryTableColumns } from "../lib/table-columns";
import { inventoryFormFields } from "../lib/form-fields";
import SearchInput from "../components/search-input";
import HorizontalBarGraph from "../components/horizontal-bar-graph";
import BarGraph from "../components/bar-graph";
import { Modal } from "../components/ui/modal";
import SpreadSheetUploadFlow from "../components/spread-sheet-upload-flow";
import { inventoryColumns } from "../lib/spreadsheet-upload-config";
import ItemDetailsDrawer from "../components/item-details-drawer";
import FormDrawer, { Field } from "../components/form-drawer";
import { RefreshToolbarButton } from "../components/refresh-toolbar-button";
import {
  bulkUpsertInventories,
  createInventory,
  getInventories,
  getInventoryStatistics,
  getWarehouses,
  updateInventory,
  CreateInventoryPayload,
  Inventory as InventoryItem,
  InventoryStatistics,
  Warehouse,
} from "../lib/api";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getBackendErrorMessage } from "../lib/utils";
import { format } from "date-fns";
import { Warehouse as WarehouseIcon } from "lucide-react";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import * as XLSX from "xlsx";

/** How inventory list + stats should be scoped to the warehouse API. */
type InventoryDataQuery =
  | { status: "pending" }
  | { status: "empty" }
  | { status: "scoped"; warehouseId: string }
  | { status: "all" };

/**
 * Reads a form or spreadsheet number. Grouping commas ("25,000") are kept as
 * part of the number; text that is not a number returns null so it is not saved as 0.
 */
function parseInventoryNumber(raw: unknown): number | null {
  if (raw === null || raw === undefined) return null;
  const text = String(raw).trim().replace(/,/g, "").replace(/\s/g, "");
  if (text === "") return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : null;
}

function inventoryNumber(raw: unknown, fallback = 0): number {
  return parseInventoryNumber(raw) ?? fallback;
}

const INVENTORY_NUMBER_FIELDS: { key: string; label: string }[] = [
  { key: "unitWeight", label: "Unit weight" },
  { key: "value", label: "Unit value" },
  { key: "hireRatePerWeek", label: "Hire rate" },
  { key: "availableQuantity", label: "Available quantity" },
  { key: "damagedQuantity", label: "Damaged quantity" },
  { key: "rentedQuantity", label: "Rented quantity" },
  { key: "reservedQuantity", label: "Reserved quantity" },
  { key: "lostQuantity", label: "Lost quantity" },
];

function inventoryTotalValue(item: {
  value?: number | string | null;
  totalQuantity?: number | string | null;
}): number {
  const unit = Number(item.value);
  const qty = Number(item.totalQuantity);
  return (
    Math.round(
      (Number.isFinite(unit) ? unit : 0) *
        (Number.isFinite(qty) ? qty : 0) *
        100,
    ) / 100
  );
}

function warehouseIdForInventoryApi(q: InventoryDataQuery): string | undefined {
  if (q.status === "scoped") return q.warehouseId;
  if (q.status === "all") return undefined;
  return undefined;
}

const Inventory: React.FC = () => {
  const { user } = useUser();
  const canUpdateInventory = user?.role === "WAREHOUSE" || user?.role === "ADMIN";
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedItemDetails, setSelectedItemDetails] = useState<InventoryItem | null>(null);

  const [showFileUploader, setShowFileUploader] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(
    location.state?.id ?? "",
  );
  /** When true, user explicitly chose "All warehouses" in the filter. */
  const [viewsAllWarehouses, setViewsAllWarehouses] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [updatingInventoryId, setUpdatingInventoryId] = useState<string | null>(null);
  const [isUpdatingCell, setIsUpdatingCell] = useState(false);
  const [formValues, setFormValues] = useState<{ [key: string]: unknown } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);

  const warehousesQuery = useQuery({
    queryKey: ["warehouses", { page: 1, limit: 1000, search: "" }],
    queryFn: async () => getWarehouses(1, 1000, ""),
  });

  const warehouses: Warehouse[] = warehousesQuery.data?.data ?? [];

  const tableColumns = useMemo(
    () =>
      canUpdateInventory
        ? inventoryTableColumns
        : inventoryTableColumns.map((col) => ({ ...col, editable: false })),
    [canUpdateInventory],
  );

  const visibleWarehouses = useMemo(() => {
    if (!user || user.role === "ADMIN") return warehouses;
    const norm = (v: string | null | undefined) => (v ?? "").trim().toLowerCase();
    const uCountry = norm(user.country);
    const uRegion = norm(user.region);
    const uDistrict = norm(user.district);
    const uWard = norm(user.ward);
    const uStreet = norm(user.street);
    if (!uCountry && !uRegion && !uDistrict && !uWard && !uStreet) {
      return warehouses;
    }
    return warehouses.filter((w) => {
      if (uCountry && norm(w.country) !== uCountry) return false;
      if (uRegion && norm(w.region) !== uRegion) return false;
      if (uDistrict && norm(w.district) !== uDistrict) return false;
      if (uWard && norm(w.ward) !== uWard) return false;
      if (uStreet && norm(w.street) !== uStreet) return false;
      return true;
    });
  }, [warehouses, user]);

  const defaultWarehouseId = useMemo(() => {
    if (visibleWarehouses.length === 0) return "";
    let best = visibleWarehouses[0];
    let bestWeight = best.totalWeight ?? 0;
    for (const w of visibleWarehouses) {
      const tw = w.totalWeight ?? 0;
      if (tw > bestWeight) {
        bestWeight = tw;
        best = w;
      }
    }
    return best.id;
  }, [visibleWarehouses]);

  const warehouseFilterOptions = useMemo(() => {
    const opts = visibleWarehouses.map((warehouse) => ({
      label: warehouse.name,
      value: warehouse.id,
    }));
    return [{ label: "All", value: "All" }, ...opts];
  }, [visibleWarehouses]);

  const inventoryDataQuery = useMemo((): InventoryDataQuery => {
    if (warehousesQuery.isLoading) return { status: "pending" };
    if (visibleWarehouses.length === 0) return { status: "empty" };
    if (selectedWarehouse) return { status: "scoped", warehouseId: selectedWarehouse };
    if (viewsAllWarehouses) return { status: "all" };
    return { status: "scoped", warehouseId: defaultWarehouseId };
  }, [
    warehousesQuery.isLoading,
    visibleWarehouses.length,
    selectedWarehouse,
    viewsAllWarehouses,
    defaultWarehouseId,
  ]);

  const warehouseSelectDisplayValue =
    viewsAllWarehouses
      ? "All"
      : selectedWarehouse || defaultWarehouseId;

  const handleWarehouseFilterChange = (value: string) => {
    if (value === "All") {
      setSelectedWarehouse("");
      setViewsAllWarehouses(true);
    } else {
      setSelectedWarehouse(value);
      setViewsAllWarehouses(false);
    }
  };

  const handleCloseFileUploader = () => {
    if (bulkUploading) return;
    setShowFileUploader(false);
  };

  const actions = [
    {
      label: "View details",
      icon: Icons.Preview,
      onClick: (row: any) => {
        setSelectedItemDetails(row);
      },
    },
  ];

  const inventoriesQuery = useQuery({
    queryKey: [
      "inventories",
      {
        page: currentPage,
        limit: pageSize,
        search: searchTerm,
        warehouseId: warehouseIdForInventoryApi(inventoryDataQuery) ?? "__all__",
      },
    ],
    queryFn: async () =>
      getInventories(
        currentPage,
        pageSize,
        searchTerm,
        warehouseIdForInventoryApi(inventoryDataQuery),
      ),
    enabled: inventoryDataQuery.status === "scoped" || inventoryDataQuery.status === "all",
    placeholderData: keepPreviousData,
  });

  const statisticsQuery = useQuery<InventoryStatistics>({
    queryKey: ["inventories", "statistics", { warehouseId: warehouseIdForInventoryApi(inventoryDataQuery) ?? "__all__" }],
    queryFn: async () => getInventoryStatistics(warehouseIdForInventoryApi(inventoryDataQuery)),
    enabled: inventoryDataQuery.status === "scoped" || inventoryDataQuery.status === "all",
  });

  const inventories: InventoryItem[] = useMemo(
    () =>
      (inventoriesQuery.data?.data ?? []).map((item) => ({
        ...item,
        totalValue: inventoryTotalValue(item),
      })),
    [inventoriesQuery.data],
  );
  const totalItems = inventoriesQuery.data?.meta.total ?? 0;
  const totalPages = inventoriesQuery.data?.meta.totalPages ?? 1;
  const statistics = statisticsQuery.data ?? null;

  // Apply warehouse from navigation once, then clear location.state so the
  // dropdown can change freely afterward.
  useEffect(() => {
    if (visibleWarehouses.length === 0) return;

    const navId =
      typeof (location.state as { id?: string } | null)?.id === "string"
        ? (location.state as { id: string }).id
        : undefined;
    if (!navId) return;

    if (visibleWarehouses.some((w) => w.id === navId)) {
      setSelectedWarehouse(navId);
      setViewsAllWarehouses(false);
    }

    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
  }, [
    location.state,
    location.pathname,
    location.search,
    visibleWarehouses,
    navigate,
  ]);

  // Default to highest-weight warehouse when nothing is selected / selection invalid
  useEffect(() => {
    if (visibleWarehouses.length === 0 || !defaultWarehouseId) return;
    if (viewsAllWarehouses) return;

    const inVisible = (id: string) => visibleWarehouses.some((w) => w.id === id);
    if (selectedWarehouse && inVisible(selectedWarehouse)) return;

    setSelectedWarehouse(defaultWarehouseId);
    setViewsAllWarehouses(false);
  }, [
    defaultWarehouseId,
    visibleWarehouses,
    selectedWarehouse,
    viewsAllWarehouses,
  ]);

  // Reset pagination when the effective warehouse filter changes
  useEffect(() => {
    if (inventoryDataQuery.status === "pending") return;
    setCurrentPage(1);
  }, [inventoryDataQuery]);

  // Debounce raw search input and apply to `searchTerm` after a short delay
  useEffect(() => {
    const handler = setTimeout(() => setSearchTerm(searchInput), 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // When search changes, reset to page 1 (query will refetch).
  useEffect(() => {
    if (currentPage !== 1) setCurrentPage(1);
  }, [searchTerm]);

  // Handler for inline editing that saves to API
  const handleCellEdit = async (
    row: any,
    columnAccessor: string,
    newValue: string,
    oldValue: any,
  ) => {
    const inventoryId = row.id;
    if (!inventoryId) {
      toast.error("Inventory ID not found");
      throw new Error("Inventory ID not found");
    }

    if (!canUpdateInventory) {
      toast.error("Only users with Warehouse role can update inventory items.");
      throw new Error("Permission denied");
    }

    // Don't save if value hasn't changed
    if (String(newValue) === String(oldValue)) {
      return;
    }

    setUpdatingInventoryId(inventoryId);
    setIsUpdatingCell(true);

    try {
      // Map column accessor to API field name and convert value type
      const toNumber = (value: unknown, fallback = 0) =>
        inventoryNumber(value, fallback);

      // Get the current inventory item from state to ensure we have all fields
      const currentInventory = inventories.find((inv) => inv.id === inventoryId);
      if (!currentInventory) {
        throw new Error("Inventory item not found in state");
      }

      // Build the update payload with all current inventory fields (length is string per API)
      const payload: any = {
        warehouseId: currentInventory.warehouseId,
        itemCode: currentInventory.itemCode,
        description: currentInventory.description,
        unitWeight: toNumber(currentInventory.unitWeight, 0),
        length: String(currentInventory.length ?? "").trim(),
        value: toNumber(currentInventory.value, 0),
        hireRatePerWeek: toNumber(currentInventory.hireRatePerWeek, 0),
        // Always coerce numeric fields so untouched values are not sent as strings.
        availableQuantity: toNumber(currentInventory.availableQuantity, 0),
        damagedQuantity: toNumber(currentInventory.damagedQuantity, 0),
        rentedQuantity: toNumber(currentInventory.rentedQuantity, 0),
        reservedQuantity: toNumber(currentInventory.reservedQuantity, 0),
        lostQuantity: toNumber(currentInventory.lostQuantity, 0),
      };

      // Update the specific field that was edited
      switch (columnAccessor) {
        case "itemCode":
          payload.itemCode = String(newValue).trim();
          break;
        case "description":
          payload.description = String(newValue).trim();
          break;
        case "unitWeight":
          payload.unitWeight = toNumber(newValue, 0);
          break;
        case "length":
          payload.length = String(newValue).trim();
          break;
        case "value":
          payload.value = toNumber(newValue, 0);
          break;
        case "hireRatePerWeek":
          payload.hireRatePerWeek = toNumber(newValue, 0);
          break;
        case "availableQuantity":
          payload.availableQuantity = toNumber(newValue, 0);
          break;
        case "rentedQuantity":
          payload.rentedQuantity = toNumber(newValue, 0);
          break;
        case "reservedQuantity":
          payload.reservedQuantity = toNumber(newValue, 0);
          break;
        case "damagedQuantity":
          payload.damagedQuantity = toNumber(newValue, 0);
          break;
        case "lostQuantity":
          payload.lostQuantity = toNumber(newValue, 0);
          break;
        default:
          toast.error(`Unknown column accessor: ${columnAccessor}`);
          throw new Error(`Unknown column accessor: ${columnAccessor}`);
      }

      // Call the API to update the inventory
      const updatedInventory = await updateInventory(inventoryId, payload);

      // Refresh list + stats via query cache
      queryClient.setQueryData(
        [
          "inventories",
          {
            page: currentPage,
            limit: pageSize,
            search: searchTerm,
            warehouseId: warehouseIdForInventoryApi(inventoryDataQuery) ?? "__all__",
          },
        ],
        (prev: any) => {
          if (!prev?.data) return prev;
          return {
            ...prev,
            data: prev.data.map((inv: InventoryItem) =>
              inv.id === inventoryId ? updatedInventory : inv,
            ),
          };
        },
      );
      await queryClient.invalidateQueries({ queryKey: ["inventories", "statistics"] });
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to update inventory"));
      await queryClient.invalidateQueries({ queryKey: ["inventories"] });
      throw error; // Re-throw to prevent DataTable from updating local state
    } finally {
      setUpdatingInventoryId(null);
      setIsUpdatingCell(false);
    }
  };

  // Get form fields with dynamic warehouse options
  const getInventoryFormFields = (): Field[] => {
    return inventoryFormFields.map((field) => {
      if (field.key === "warehouseId") {
        return {
          ...field,
          options: visibleWarehouses.map((w) => ({
            label: w.name,
            value: w.id,
          })),
        };
      }
      return field;
    });
  };

  // Handler for form submission
  const handleFormSubmit = async () => {
    if (!formValues) return;

    if (!canUpdateInventory) {
      toast.error("Only users with Warehouse role can add inventory items.");
      return;
    }

    const invalidNumber = INVENTORY_NUMBER_FIELDS.find((field) => {
      const raw = formValues[field.key];
      return (
        raw !== null &&
        raw !== undefined &&
        String(raw).trim() !== "" &&
        parseInventoryNumber(raw) === null
      );
    });
    if (invalidNumber) {
      toast.error(`${invalidNumber.label} must be a number.`);
      return;
    }

    setIsSubmitting(true);

    const payload: CreateInventoryPayload = {
      warehouseId: formValues.warehouseId as string,
      itemCode: String(formValues.itemCode || "").trim(),
      description: String(formValues.description || "").trim(),
      unitWeight: inventoryNumber(formValues.unitWeight, 0),
      length: String(formValues.length || "").trim(),
      value: inventoryNumber(formValues.value, 0),
      hireRatePerWeek: inventoryNumber(formValues.hireRatePerWeek, 0),
      availableQuantity: inventoryNumber(formValues.availableQuantity, 0),
      damagedQuantity: inventoryNumber(formValues.damagedQuantity, 0),
      rentedQuantity: inventoryNumber(formValues.rentedQuantity, 0),
      reservedQuantity: inventoryNumber(formValues.reservedQuantity, 0),
      lostQuantity: inventoryNumber(formValues.lostQuantity, 0),
    };

    // Validate required fields
    if (!payload.warehouseId || !payload.description) {
      toast.error("Please fill in all required fields (Warehouse, and Description).");
      setIsSubmitting(false);
      return;
    }

    try {
      await createInventory(payload);
      setFormValues(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["inventories"] }),
        queryClient.invalidateQueries({ queryKey: ["inventories", "statistics"] }),
      ]);
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to create inventory item"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      if (inventoryDataQuery.status === "pending") {
        toast.error("Still loading warehouses.");
        return;
      }
      if (inventoryDataQuery.status === "empty") {
        toast.error("No warehouse available to export.");
        return;
      }
      const response = await getInventories(
        1,
        2000,
        searchTerm,
        warehouseIdForInventoryApi(inventoryDataQuery),
      );
      const data = response.data ?? [];
      const doc = new jsPDF({ orientation: "portrait" });
      const titleDate = format(new Date(), "dd MMM yyyy");
      doc.setFontSize(16);
      doc.text(`Inventory Stock Count - ${titleDate}`, 14, 16);

      const headers = [
        "Description",
        "Length",
        "Qty Available",
        "Phy",
        "Diff",
        "Remark",
      ];
      const rows = data.map((item: InventoryItem) => [
        String(item.description ?? ""),
        String(item.length ?? ""),
        String(item.availableQuantity ?? ""),
        "",
        "",
        "",
      ]);

      autoTable(doc, {
        head: [headers],
        body: rows.length > 0 ? rows : [["No data", "", "", "", "", ""]],
        startY: 22,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [71, 85, 105] },
      });

      doc.save(`Inventory Stock Count - ${format(new Date(), "yyyy-MM-dd")}.pdf`);
      toast.success("PDF downloaded");
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to download PDF"));
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      if (inventoryDataQuery.status === "pending") {
        toast.error("Still loading warehouses.");
        return;
      }
      if (inventoryDataQuery.status === "empty") {
        toast.error("No warehouse available to export.");
        return;
      }

      const total = Math.max(totalItems || 0, 1);
      const response = await getInventories(
        1,
        total,
        searchTerm,
        warehouseIdForInventoryApi(inventoryDataQuery),
      );
      const data = response.data ?? [];
      if (data.length === 0) {
        toast.error("No inventory items to download");
        return;
      }

      const rows = data.map((item) => ({
        "Item Code": item.itemCode ?? "",
        Description: item.description ?? "",
        "Unit Weight (KG)": Number(item.unitWeight) || 0,
        Length: item.length ?? "",
        "Unit Value": Number(item.value) || 0,
        "Hire Rate / Week": Number(item.hireRatePerWeek) || 0,
        Available: Number(item.availableQuantity) || 0,
        Rented: Number(item.rentedQuantity) || 0,
        Reserved: Number(item.reservedQuantity) || 0,
        Damaged: Number(item.damagedQuantity) || 0,
        Lost: Number(item.lostQuantity) || 0,
        Total: Number(item.totalQuantity) || 0,
        "Total Value": inventoryTotalValue(item),
        Warehouse: item.warehouse?.name ?? "",
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Inventory");
      XLSX.writeFile(wb, `Inventory_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
      toast.success("Inventory Excel downloaded");
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to download Excel"));
    } finally {
      setDownloadingExcel(false);
    }
  };

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["inventories"] }),
      queryClient.invalidateQueries({ queryKey: ["inventories", "statistics"] }),
      queryClient.invalidateQueries({ queryKey: ["warehouses"] }),
    ]);

  const openFileUploader = () => {
    if (!canUpdateInventory) return;
    setShowFileUploader(true);
  };

  const openNewItem = () => {
    if (!canUpdateInventory) return;
    setFormValues({});
  };

  const resetWarehouseFilter = () => {
    setSelectedWarehouse(defaultWarehouseId);
    setViewsAllWarehouses(false);
  };

  const activeWarehouseName = viewsAllWarehouses
    ? "All warehouses"
    : visibleWarehouses.find((w) => w.id === warehouseSelectDisplayValue)?.name;

  const warehouseSelect = (
    <SelectInput
      value={warehouseSelectDisplayValue}
      options={warehouseFilterOptions}
      placeholder={"Warehouse"}
      onChange={handleWarehouseFilterChange}
    />
  );

  return (
    <>
      <FormDrawer
        title="Add New Inventory Item"
        buttonText="Add Item"
        formValues={formValues}
        setFormValues={setFormValues}
        fields={getInventoryFormFields()}
        handleSubmit={handleFormSubmit}
        isSubmitting={isSubmitting}
        onClose={() => {
          setFormValues(null);
        }}
      />
      <ItemDetailsDrawer
        selectedItemDetails={selectedItemDetails}
        onClose={() => {
          setSelectedItemDetails(null);
        }}
      />

      <Modal
        isOpen={showFileUploader}
        onClose={handleCloseFileUploader}
        className="max-w-5xl"
        title="Upload Excel to Add Items on Inventory"
        headerAction={warehouseSelect}
      >
        <div className="h-[calc(100vh-8rem)]">
          <SpreadSheetUploadFlow
            supportedColumns={inventoryColumns}
            onUpload={async (data) => {
              if (bulkUploading) return;

              if (!canUpdateInventory) {
                toast.error("Only users with Warehouse role can upload inventory.");
                return;
              }
              if (!selectedWarehouse) {
                toast.error("Please select a warehouse before uploading.");
                return;
              }

              const toNumber = (value: unknown, fallback = 0) =>
                inventoryNumber(value, fallback);

              const toTitleCase = (str: string) =>
                str
                  .trim()
                  .toLowerCase()
                  .replace(/\b\w/g, (c) => c.toUpperCase());

              const items = data
                .map((row) => ({
                  warehouseId: selectedWarehouse,
                  itemCode: String(row.item_code).trim(),
                  description: toTitleCase(String(row.description ?? "")),
                  unitWeight: toNumber(row.unit_weight, 0),
                  length: String(row.length).trim(),
                  ...(row.value !== undefined &&
                  row.value !== null &&
                  row.value !== ""
                    ? { value: toNumber(row.value, 0) }
                    : {}),
                  ...(row.hire_rate_per_week !== undefined &&
                  row.hire_rate_per_week !== null &&
                  row.hire_rate_per_week !== ""
                    ? { hireRatePerWeek: toNumber(row.hire_rate_per_week, 0) }
                    : {}),
                  availableQuantity: toNumber(row.available_quantity, 0),
                  damagedQuantity: toNumber(row.damaged_quantity, 0),
                  rentedQuantity: toNumber(row.rented_quantity, 0),
                  reservedQuantity: toNumber(row.reserved_quantity, 0),
                  lostQuantity: toNumber(row.lost_quantity, 0),
                }));

              if (items.length === 0) {
                toast.error(
                  "No valid rows found to upload. Make sure you mapped the required columns (Item Code and Description).",
                );
                return;
              }

              try {
                setBulkUploading(true);
                const result = await bulkUpsertInventories({ items });

                setShowFileUploader(false);
                await Promise.all([
                  queryClient.invalidateQueries({ queryKey: ["inventories"] }),
                  queryClient.invalidateQueries({ queryKey: ["inventories", "statistics"] }),
                ]);
                toast.success(`Upload complete — created ${result.createdCount}, updated ${result.updatedCount}`);
              } catch (error) {
                toast.error(getBackendErrorMessage(error, "Failed to upload inventories"));
              } finally {
                setBulkUploading(false);
              }
            }}
          />
        </div>
      </Modal>
      <PageShell
        title="Inventory"
        description="Keep full control of your equipment inventory across all building sites."
        loading={
          warehousesQuery.isLoading ||
          (inventoryDataQuery.status !== "empty" && inventoriesQuery.isLoading) ||
          (inventoryDataQuery.status !== "empty" && statisticsQuery.isLoading)
        }
        toolbar={
          <div className="flex flex-wrap items-center gap-3">
            {warehouseSelect}
            <RefreshToolbarButton
              isRefreshing={
                warehousesQuery.isFetching ||
                inventoriesQuery.isFetching ||
                statisticsQuery.isFetching
              }
              onRefresh={refresh}
            />
            <Button
              variant="outline"
              className="h-9"
              disabled={!canUpdateInventory}
              onClick={openFileUploader}
              title={!canUpdateInventory ? "Only Warehouse role can upload" : undefined}
            >
              <Icons.Document className="h-4 w-4 mr-2" />
              Upload Excel
            </Button>
            <Button
              variant="default"
              className="h-9"
              disabled={!canUpdateInventory}
              onClick={(e) => {
                e.preventDefault();
                openNewItem();
              }}
              title={!canUpdateInventory ? "Only Warehouse role can add items" : undefined}
            >
              <Icons.Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          </div>
        }
        stats={[
          {
            label: "Total Value",
            value: `TZS ${(statistics?.totals.value || 0).toLocaleString()}`,
            icon: <Icons.Inventory className="w-5 h-5 text-primary-600" />,
          },
          {
            label: "In Stock",
            value: (statistics?.totals.available || 0).toLocaleString(),
            icon: <Icons.InStock className="w-5 h-5 text-primary-600" />,
          },
          {
            label: "Rented Out",
            value: (statistics?.totals.rented || 0).toLocaleString(),
            icon: <Icons.RentedOut className="w-5 h-5 text-primary-600" />,
          },
          {
            label: "Reserved",
            value: (statistics?.totals.reserved || 0).toLocaleString(),
            icon: <Icons.Reserved className="w-5 h-5 text-primary-600" />,
          },
          {
            label: "Damaged",
            value: (statistics?.totals.damaged || 0).toLocaleString(),
            icon: <Icons.Damaged className="w-5 h-5 text-red-600" />,
          },
          {
            label: "Lost",
            value: (statistics?.totals.lost || 0).toLocaleString(),
            icon: <Icons.Lost className="w-5 h-5 text-gray-700" />,
          },
        ]}
        insights={
          <div className="grid grid-cols-1 lg:grid-cols-[1fr,400px] gap-4">
            <HorizontalBarGraph
              graphTitle="Inventory by Warehouse"
              data={statistics?.byWarehouse.slice(0, 3).map(warehouse => ({
                category: warehouse.warehouseName,
                gap: 0.2,
                values: [
                  { label: "Available", value: warehouse.totals.available, fillColor: "#bce3ff" },
                  { label: "Rented", value: warehouse.totals.rented, fillColor: "#e9d5ff" },
                  { label: "Reserved", value: warehouse.totals.reserved, fillColor: "#ffd6a8" },
                  { label: "Damaged", value: warehouse.totals.damaged, fillColor: "#ffb3b3" },
                  { label: "Lost", value: warehouse.totals.lost || 0, fillColor: "#d4d4d8" },
                ],
              })) || []}
            />
            <BarGraph
              graphTitle="Stock Status Overview"
              metrics={[
                {
                  label: "In Stock",
                  count: statistics?.stockStatus.inStock || 0,
                  percentage: Math.round(((statistics?.stockStatus.inStock || 0) / (statistics?.totalItems || 1)) * 100),
                },
                {
                  label: "Low Stock",
                  count: statistics?.stockStatus.lowStock || 0,
                  percentage: Math.round(((statistics?.stockStatus.lowStock || 0) / (statistics?.totalItems || 1)) * 100),
                },
                {
                  label: "Out of Stock",
                  count: statistics?.stockStatus.outOfStock || 0,
                  percentage: Math.round(((statistics?.stockStatus.outOfStock || 0) / (statistics?.totalItems || 1)) * 100),
                },
              ]}
            />
          </div>
        }
        mobile={{
          onRefresh: refresh,
          primaryAction: {
            label: "Add Item",
            icon: Icons.Plus,
            onClick: openNewItem,
            disabled: !canUpdateInventory,
          },
          actions: [
            {
              label: "Upload Excel",
              icon: Icons.Document,
              onClick: openFileUploader,
              disabled: !canUpdateInventory,
            },
            {
              label: downloadingExcel ? "Downloading..." : "Download Excel",
              icon: Icons.Download,
              onClick: () => void handleDownloadExcel(),
              disabled: downloadingExcel || totalItems === 0,
            },
            {
              label: downloadingPdf ? "Downloading..." : "Download PDF",
              icon: Icons.Download,
              onClick: () => void handleDownloadPdf(),
              disabled: downloadingPdf,
            },
          ],
          filters: <FilterField label="Warehouse">{warehouseSelect}</FilterField>,
          activeFilterCount: warehouseSelectDisplayValue !== defaultWarehouseId ? 1 : 0,
          onResetFilters: resetWarehouseFilter,
          headerContent: activeWarehouseName ? (
            <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
              <WarehouseIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" />
              <span className="truncate">{activeWarehouseName}</span>
            </span>
          ) : undefined,
        }}
      >
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <SearchInput
            searchTerm={searchInput}
            onSearchChange={(value) => setSearchInput(value)}
            placeholder="Search items..."
          />
          <DesktopOnly>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                className="h-9"
                disabled={downloadingExcel || totalItems === 0}
                onClick={() => void handleDownloadExcel()}
              >
                <Icons.Download className="h-4 w-4 mr-2" />
                {downloadingExcel ? "Downloading..." : "Download Excel"}
              </Button>
              <Button
                variant="outline"
                className="h-9"
                disabled={downloadingPdf}
                onClick={handleDownloadPdf}
              >
                <Icons.Download className="h-4 w-4 mr-2" />
                {downloadingPdf ? "Downloading..." : "Download PDF"}
              </Button>
            </div>
          </DesktopOnly>
        </div>
        <DataTable
          columns={tableColumns}
          data={inventories as any[]}
          setSearchTerm={setSearchTerm}
          actions={actions}
          loading={inventoriesQuery.isFetching}
          onCellEdit={handleCellEdit}
          pagination={{
            page: currentPage,
            pageSize,
            totalItems,
            totalPages,
            onPageChange: (page) => {
              setCurrentPage(page);
            },
            onPageSizeChange: (size) => {
              setPageSize(size);
            },
          }}
        />
      </PageShell>
    </>
  );
};

export default Inventory;